import { useEffect, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { SetRequired } from 'type-fest';

import {
  InputFieldItem,
  InputFieldItemProps,
} from '@/components/form/formAwareInputs/FormAwareInputRepeater/InputFieldItem';
import { DuplicatedFieldGroup } from '@/components/form/formAwareInputs/formAwareRepeater/FormAwareRepeater.types';
import { Card } from '@/components/layout/Card/Card';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { useAiSuggestionsMatcherContext } from '@/modules/aiSuggestions/AISuggestionsMatcher/context/aiSuggestionsMatcher.context';

import { SuggestionLocatorPosition } from '../FormAwareInput.types';

export interface DuplicatedInputFieldGroupProps<
  FormShape extends FieldValues,
  FieldArrayKeyName extends string,
> extends Omit<
    InputFieldItemProps<FormShape, FieldArrayKeyName>,
    'i' | 'value' | 'onDeleteItemClick'
  > {
  group: SetRequired<
    DuplicatedFieldGroup<FormShape, FieldArrayKeyName>,
    'existingFieldWithIndex' | 'suggestedFieldWithIndex'
  >;
  fieldArrayKeyName: FieldArrayKeyName;
}

export function DuplicatedInputFieldGroup<
  FormShape extends FieldValues,
  FieldArrayKeyName extends string,
>({
  group,
  fieldArrayKeyName,
  ...rest
}: DuplicatedInputFieldGroupProps<FormShape, FieldArrayKeyName>) {
  const trackUserEvent = useTrackUserEvent();

  const { resolveDuplicate } = useAiSuggestionsMatcherContext();

  const {
    fieldArrayProps: { remove },
  } = rest;

  const [selectedIndex, setSelectedIndex] = useState<number>(
    group.suggestedFieldWithIndex.index
  );

  useEffect(() => {
    setSelectedIndex(group.suggestedFieldWithIndex.index);
  }, [group.suggestedFieldWithIndex.index]);

  return (
    <Card
      variant="filled-teal"
      sx={{
        p: 2,
        pt: 2,
        pr: 1,
        mt: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'visible',
      }}
      data-testid="duplicated-repeater-input-group"
    >
      <InputFieldItem<FormShape, FieldArrayKeyName>
        {...rest}
        key={group.suggestedFieldWithIndex.field[fieldArrayKeyName]}
        i={group.suggestedFieldWithIndex.index}
        value={group.suggestedFieldWithIndex.field}
        fieldArrayKeyName={fieldArrayKeyName}
        additionalItemRendererProps={{
          hideLabelOverride: false,
          showAISparkleIcon: true,
          locatorPosition: SuggestionLocatorPosition.RIGHT,
          duplicateResolutionConfigs: {
            isSelected: selectedIndex === group.suggestedFieldWithIndex.index,
            onSelect: () =>
              setSelectedIndex(group.suggestedFieldWithIndex.index),
            onConfirm: () => {
              trackUserEvent('ai_suggestion resolve duplicates', {
                removedVariant: 'suggested',
              });
              // Confirming the suggested field removes the existing field from the form array.
              remove(group.existingFieldWithIndex.index);
              resolveDuplicate(group.arrayFieldFullPath);
            },
          },
        }}
        showDeleteButton={false}
      />
      <InputFieldItem<FormShape, FieldArrayKeyName>
        {...rest}
        key={group.existingFieldWithIndex.field[fieldArrayKeyName]}
        i={group.existingFieldWithIndex.index}
        value={group.existingFieldWithIndex.field}
        fieldArrayKeyName={fieldArrayKeyName}
        additionalItemRendererProps={{
          hideLabelOverride: true,
          hideLocateSuggestionIconOverride: true,
          locatorPosition: SuggestionLocatorPosition.RIGHT,
          duplicateResolutionConfigs: {
            isSelected: selectedIndex === group.existingFieldWithIndex.index,
            onSelect: () =>
              setSelectedIndex(group.existingFieldWithIndex.index),
            onConfirm: () => {
              // Confirming the existing field removes the suggested field from the form array.
              remove(group.suggestedFieldWithIndex.index);
              resolveDuplicate(group.arrayFieldFullPath);
              trackUserEvent('ai_suggestion resolve duplicates', {
                removedVariant: 'existing',
              });
            },
          },
        }}
        showDeleteButton={false}
      />
    </Card>
  );
}
